import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { ChevronDown, ChevronRight, Eye, EyeOff, Plus, Trash2, Edit2, Check, X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { EnvironmentVariable } from '@/types/conversations'

interface CustomVariablesSectionProps {
  projectId: number
  variables: EnvironmentVariable[]
  isLive: boolean
}

export function CustomVariablesSection({ projectId, variables, isLive }: CustomVariablesSectionProps) {
  const { t } = useTranslation('conversations')
  const [isSectionExpanded, setIsSectionExpanded] = useState(false)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [visibleValues, setVisibleValues] = useState<Set<number>>(new Set())
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [variableToDelete, setVariableToDelete] = useState<EnvironmentVariable | null>(null)

  // Form state for adding/editing
  const [formData, setFormData] = useState({
    key: '',
    value: '',
    description: ''
  })

  const toggleValueVisibility = (id: number) => {
    setVisibleValues(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const resetForm = () => {
    setFormData({ key: '', value: '', description: '' })
    setIsAdding(false)
    setEditingId(null)
  }

  const handleAdd = async () => {
    if (!formData.key.trim()) {
      toast.error(t('integrations.toasts.keyRequired'))
      return
    }

    try {
      const response = await fetch(`/projects/${projectId}/environment_variables`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          key: formData.key.toUpperCase(),
          value: formData.value,
          description: formData.description,
          is_live: isLive
        })
      })

      const data = await response.json()

      if (data.success) {
        toast.success(t('integrations.toasts.secretAddSuccess'))
        resetForm()
        router.reload()
      } else {
        toast.error(data.errors?.join(', ') || t('integrations.toasts.secretAddError'))
      }
    } catch (error) {
      console.error('Error adding secret:', error)
      toast.error(t('integrations.toasts.unexpectedError'))
    }
  }

  const handleUpdate = async (id: number) => {
    try {
      const response = await fetch(`/projects/${projectId}/environment_variables/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          key: formData.key.toUpperCase(),
          value: formData.value,
          description: formData.description
        })
      })

      const data = await response.json()

      if (data.success) {
        toast.success(t('integrations.toasts.secretUpdateSuccess'))
        resetForm()
        router.reload()
      } else {
        toast.error(data.errors?.join(', ') || t('integrations.toasts.secretUpdateError'))
      }
    } catch (error) {
      console.error('Error updating secret:', error)
      toast.error(t('integrations.toasts.unexpectedError'))
    }
  }

  const openDeleteDialog = (variable: EnvironmentVariable) => {
    setVariableToDelete(variable)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!variableToDelete) return

    try {
      const response = await fetch(`/projects/${projectId}/environment_variables/${variableToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      })

      const data = await response.json()

      if (data.success) {
        toast.success(t('integrations.toasts.secretDeleteSuccess'))
        setDeleteDialogOpen(false)
        setVariableToDelete(null)
        router.reload()
      } else {
        toast.error(data.errors?.join(', ') || t('integrations.toasts.secretDeleteError'))
      }
    } catch (error) {
      console.error('Error deleting secret:', error)
      toast.error(t('integrations.toasts.unexpectedError'))
    }
  }

  const startEdit = (variable: EnvironmentVariable) => {
    setEditingId(variable.id)
    setExpandedId(variable.id)
    setFormData({
      key: variable.key,
      value: variable.value,
      description: variable.description || ''
    })
    setIsAdding(false)
  }

  return (
    <div className="border rounded-lg bg-card overflow-hidden mb-4">
      {/* Section Header - Always Visible */}
      <button
        type="button"
        onClick={() => setIsSectionExpanded(!isSectionExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-accent transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="text-left">
            <h3 className="font-semibold text-sm text-foreground">
              {t('integrations.customSecrets.title')}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t('integrations.customSecrets.count', { count: variables.length })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isSectionExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isSectionExpanded && (
        <div className="border-t p-4 bg-muted/50 space-y-3">
          {/* Add Secret Form - Shown at top when adding */}
          {isAdding && (
            <div className="border rounded-lg bg-card p-4 space-y-3">
              <h4 className="font-medium text-sm">{t('integrations.customSecrets.addTitle')}</h4>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  {t('integrations.customSecrets.keyLabel')}
                </label>
                <Input
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value.toUpperCase() })}
                  placeholder="DATABASE_URL"
                  className="font-mono text-sm"
                  autoFocus
                />
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  {t('integrations.customSecrets.valueLabel')}
                </label>
                <Input
                  type="password"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  placeholder="••••••••"
                  className="font-mono text-sm"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  {t('integrations.customSecrets.descriptionLabel')}
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('integrations.customSecrets.descriptionPlaceholder')}
                  rows={2}
                  className="text-sm"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleAdd}
                  size="sm"
                  variant="default"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t('integrations.customSecrets.addButton')}
                </Button>
                <Button
                  onClick={resetForm}
                  size="sm"
                  variant="outline"
                >
                  <X className="h-4 w-4 mr-1" />
                  {t('integrations.customSecrets.cancelButton')}
                </Button>
              </div>
            </div>
          )}

          {/* Header with Add Button */}
          {variables.length > 0 && !isAdding && (
            <div className="flex items-center justify-end mb-2">
              <Button
                onClick={() => setIsAdding(true)}
                size="sm"
                variant="outline"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('integrations.customSecrets.addButton')}
              </Button>
            </div>
          )}

          {/* Empty State */}
          {variables.length === 0 && !isAdding && (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground mb-4">
                {t('integrations.customSecrets.emptyState')}
              </p>
              <Button
                onClick={() => setIsAdding(true)}
                size="sm"
                variant="outline"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('integrations.customSecrets.addButton')}
              </Button>
            </div>
          )}

          {/* Secret Cards */}
          {variables.map((variable) => {
        const isExpanded = expandedId === variable.id
        const isEditing = editingId === variable.id

        return (
          <div key={variable.id} className="border rounded-lg bg-card overflow-hidden">
            {/* Card Header - Always Visible */}
            <button
              type="button"
              onClick={() => setExpandedId(isExpanded ? null : variable.id)}
              disabled={isEditing}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-3 flex-1 text-left">
                <div className="flex-1">
                  <h4 className="font-semibold text-sm text-foreground font-mono">
                    {variable.key}
                  </h4>
                  {variable.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {variable.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </button>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="border-t p-4 bg-muted/50">
                {isEditing ? (
                  // Edit Mode
                  <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      {t('integrations.customSecrets.keyLabel')}
                    </label>
                    <Input
                      value={formData.key}
                      onChange={(e) => setFormData({ ...formData, key: e.target.value.toUpperCase() })}
                      placeholder="DATABASE_URL"
                      className="font-mono text-sm"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      {t('integrations.customSecrets.valueLabel')}
                    </label>
                    <Input
                      type="password"
                      value={formData.value}
                      onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                      placeholder="••••••••"
                      className="font-mono text-sm"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                      {t('integrations.customSecrets.descriptionLabel')}
                    </label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder={t('integrations.customSecrets.descriptionPlaceholder')}
                      rows={2}
                      className="text-sm"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleUpdate(variable.id)}
                      size="sm"
                      variant="default"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      {t('integrations.customSecrets.saveButton')}
                    </Button>
                    <Button
                      onClick={resetForm}
                      size="sm"
                      variant="outline"
                    >
                      <X className="h-4 w-4 mr-1" />
                      {t('integrations.customSecrets.cancelButton')}
                    </Button>
                  </div>
                </div>
                ) : (
                  // View Mode
                  <div className="space-y-3">
                    <div>
                      <div className="text-xs font-medium text-muted-foreground mb-1">
                        {t('integrations.customSecrets.valueLabel')}
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono bg-muted px-2.5 py-1.5 rounded border flex-1">
                          {visibleValues.has(variable.id) ? variable.value : '••••••••'}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleValueVisibility(variable.id)
                          }}
                          title={visibleValues.has(variable.id) ? t('integrations.customSecrets.hideValue') : t('integrations.customSecrets.showValue')}
                        >
                          {visibleValues.has(variable.id) ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation()
                          startEdit(variable)
                        }}
                        size="sm"
                        variant="outline"
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        {t('integrations.customSecrets.editButton')}
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation()
                          openDeleteDialog(variable)
                        }}
                        size="sm"
                        variant="destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        {t('integrations.customSecrets.deleteButton')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('integrations.customSecrets.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription dangerouslySetInnerHTML={{
              __html: t('integrations.customSecrets.deleteDescription', { key: variableToDelete?.key })
            }} />
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('integrations.dialogs.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('integrations.customSecrets.deleteButton')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
